import { useCallback, useEffect, useRef, useState } from 'react';
import { SegmentedControl } from '../../ui/Navigation';
import { useStrings } from '../../tenant/TenantProvider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { storage } from '../../lib/storage';

const SPLIT_KEY = 'workspace-split';
const MIN_LEFT = 28;
const MAX_LEFT = 65;
const MIN_CONSOLE = 18;
const MAX_CONSOLE = 70;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function useSplit() {
  const [split, setSplit] = useState(() => {
    const saved = storage.getJson(SPLIT_KEY);
    return {
      left: clamp(Number(saved?.left) || 42, MIN_LEFT, MAX_LEFT),
      console: clamp(Number(saved?.console) || 36, MIN_CONSOLE, MAX_CONSOLE),
    };
  });

  useEffect(() => {
    const timer = setTimeout(() => storage.setJson(SPLIT_KEY, split), 300);
    return () => clearTimeout(timer);
  }, [split]);

  return [split, setSplit];
}

function Splitter({ orientation, label, onDrag, onReset, value, min, max }) {
  const start = (event) => {
    event.preventDefault();
    const onMove = (moveEvent) => onDrag(moveEvent);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.classList.remove('is-resizing', `is-resizing--${orientation}`);
    };
    document.body.classList.add('is-resizing', `is-resizing--${orientation}`);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div
      className={`splitter splitter--${orientation}`}
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation={orientation === 'vertical' ? 'vertical' : 'horizontal'}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      onPointerDown={start}
      onDoubleClick={onReset}
      onKeyDown={(event) => {
        const keys = orientation === 'vertical' ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        onDrag(null, event.key === keys[0] ? -2 : 2);
      }}
    />
  );
}

export function WorkspaceLayout({ header, notice, statement, editor, console: consolePanel }) {
  const t = useStrings();
  const compact = useMediaQuery('(max-width: 900px)');
  const [mobileView, setMobileView] = useState('problem');
  const [split, setSplit] = useSplit();
  const bodyRef = useRef(null);
  const rightRef = useRef(null);

  const dragLeft = useCallback(
    (event, step) => {
      setSplit((current) => {
        if (step) return { ...current, left: clamp(current.left + step, MIN_LEFT, MAX_LEFT) };
        const rect = bodyRef.current.getBoundingClientRect();
        return { ...current, left: clamp(((event.clientX - rect.left) / rect.width) * 100, MIN_LEFT, MAX_LEFT) };
      });
    },
    [setSplit]
  );

  const dragConsole = useCallback(
    (event, step) => {
      setSplit((current) => {
        if (step) return { ...current, console: clamp(current.console - step, MIN_CONSOLE, MAX_CONSOLE) };
        const rect = rightRef.current.getBoundingClientRect();
        return { ...current, console: clamp(((rect.bottom - event.clientY) / rect.height) * 100, MIN_CONSOLE, MAX_CONSOLE) };
      });
    },
    [setSplit]
  );

  if (compact) {
    return (
      <div className="workspace workspace--compact">
        {header}
        {notice}
        <div className="workspace__switcher">
          <SegmentedControl
            label={t('workspace.mobileProblem')}
            value={mobileView}
            onChange={setMobileView}
            options={[
              { value: 'problem', label: t('workspace.mobileProblem') },
              { value: 'code', label: t('workspace.mobileCode') },
              { value: 'result', label: t('workspace.mobileResult') },
            ]}
          />
        </div>
        <div className="workspace__single">
          {mobileView === 'problem' && statement}
          {mobileView === 'code' && editor}
          {mobileView === 'result' && consolePanel}
        </div>
      </div>
    );
  }

  return (
    <div className="workspace">
      {header}
      {notice}
      <div ref={bodyRef} className="workspace__body" style={{ gridTemplateColumns: `${split.left}% 8px minmax(0, 1fr)` }}>
        <div className="workspace__left">{statement}</div>
        <Splitter
          orientation="vertical"
          label={t('workspace.resizeHandle')}
          value={split.left}
          min={MIN_LEFT}
          max={MAX_LEFT}
          onDrag={dragLeft}
          onReset={() => setSplit((current) => ({ ...current, left: 42 }))}
        />
        <div ref={rightRef} className="workspace__right" style={{ gridTemplateRows: `minmax(0, 1fr) 8px ${split.console}%` }}>
          {editor}
          <Splitter
            orientation="horizontal"
            label={t('workspace.resizeHandle')}
            value={split.console}
            min={MIN_CONSOLE}
            max={MAX_CONSOLE}
            onDrag={dragConsole}
            onReset={() => setSplit((current) => ({ ...current, console: 36 }))}
          />
          {consolePanel}
        </div>
      </div>
    </div>
  );
}
